'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { setIsBottomNavVisible } from '@/store/reducers/appSlice';
import { useUserLocale } from '@/hooks/useUserLocale';
import clsx from 'clsx';

import {
  HomeIcon as HomeOutline,
  UserGroupIcon as UserGroupOutline,
  NewspaperIcon as NewspaperOutline,
  ClipboardDocumentCheckIcon as ClipboardOutline,
  BookOpenIcon as BookOpenOutline,
} from '@heroicons/react/24/outline';

import {
  HomeIcon as HomeSolid,
  UserGroupIcon as UserGroupSolid,
  NewspaperIcon as NewspaperSolid,
  ClipboardDocumentCheckIcon as ClipboardSolid,
  BookOpenIcon as BookOpenSolid,
} from '@heroicons/react/24/solid';

const HIDE_ROUTES: (string | RegExp)[] = [
  '/',
  '/login',
  '/onboard',
  /^\/welcome\/.*/,
  /^\/webview\/.*/,
  /^\/practice\/.*/,
  /^\/exam\/.*/,
  /^\/chat\/.*/,
  /^\/story\/.*/,
  /^\/page\/.*/,
  /^\/welcome\/tryit/,
  /^\/welcome\/complete-account/,
];

interface NavItem {
  id: string;
  labelKey: keyof ReturnType<typeof useUserLocale>['t']['nav']['bottomBar'];
  route: string;
  matchPrefix: string;
  isFeatured?: boolean;
  OutlineIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  SolidIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    labelKey: 'home',
    route: '/dashboard',
    matchPrefix: '/dashboard',
    OutlineIcon: HomeOutline,
    SolidIcon: HomeSolid,
  },
  {
    id: 'contacts',
    labelKey: 'contacts',
    route: '/contacts',
    matchPrefix: '/contacts',
    OutlineIcon: UserGroupOutline,
    SolidIcon: UserGroupSolid,
  },
  {
    id: 'feed',
    labelKey: 'feed',
    route: '/learning-feed',
    matchPrefix: '/learning-feed',
    isFeatured: true,
    OutlineIcon: NewspaperOutline,
    SolidIcon: NewspaperSolid,
  },
  {
    id: 'inbox',
    labelKey: 'inbox',
    route: '/inbox',
    matchPrefix: '/inbox',
    OutlineIcon: ClipboardOutline,
    SolidIcon: ClipboardSolid,
  },
  {
    id: 'vocab',
    labelKey: 'vocab',
    route: '/vocab',
    matchPrefix: '/vocab',
    OutlineIcon: BookOpenOutline,
    SolidIcon: BookOpenSolid,
  },
];

export default function BottomNavBar() {
  const pathname = usePathname();
  const dispatch = useDispatch();
  const { t } = useUserLocale();
  const currentUser = useSelector((state: RootState) => state.app.currentUser);
  const isFlutterWebview = useSelector((state: RootState) => state.app.isFlutterWebview);

  const shouldHide = useMemo(() => {
    if (!currentUser) return true;

    for (const route of HIDE_ROUTES) {
      if (typeof route === 'string') {
        if (pathname === route) return true;
      } else {
        if (route.test(pathname)) return true;
      }
    }

    return false;
  }, [pathname, currentUser]);

  useEffect(() => {
    dispatch(setIsBottomNavVisible(!shouldHide));
    return () => {
      dispatch(setIsBottomNavVisible(false));
    };
  }, [shouldHide, dispatch]);

  if (shouldHide) {
    return null;
  }

  return (
    <nav
      className={clsx(
        'fixed bottom-[-1px] left-0 right-0 z-30 pb-[1px]',
        'bottom-nav-bar',
        'bg-white dark:bg-neutral-900',
        'border-t border-gray-100 dark:border-neutral-800',
        'sm:rounded-tl-xl sm:rounded-tr-xl',
        'shadow-[0_-1px_12px_rgba(0,0,0,0.08)]',
        'sm:max-w-xl mx-auto'
      )}
    >
      <div className="max-w-xl mx-auto px-2">
        <div className="flex items-center justify-around h-[60px]">
          {NAV_ITEMS.map((item) => {
            const isActive = pathname.startsWith(item.matchPrefix);
            const Icon = isActive ? item.SolidIcon : item.OutlineIcon;
            const label = t.nav.bottomBar[item.labelKey];

            if (item.isFeatured) {
              return (
                <Link
                  key={item.id}
                  href={item.route}
                  className={clsx("flex flex-col items-center justify-center gap-0.5 flex-1 py-1 group", isActive ? 'mt-[-0.9rem]' : 'mt-[-1rem]')}
                  aria-label={label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <div
                    className={clsx(
                      'flex items-center justify-center w-14 h-10 rounded-xl transition-all duration-200',
                      isActive
                        ? 'scale-110 mb-[0.1rem] bg-gradient-to-br from-purple-500 to-pink-500 shadow-[0_-1px_12px_rgba(180,0,160,0.48)]'//'shadow-lg shadow-purple-200 dark:shadow-purple-900/40'
                        : 'bg-gradient-to-br from-purple-500 to-pink-500 shadow-[0_-1px_12px_rgba(0,0,0,0.18)] group-hover:from-purple-600 group-hover:to-pink-600'
                    )}
                  >
                    <Icon
                      className={clsx(
                        'w-6 h-6 transition-colors duration-200',
                        isActive ? 'text-white' : 'text-white/90'
                      )}
                    />
                  </div>
                  <span
                    className={clsx(
                      'mt-0.5 text-[10px] font-semibold leading-none transition-colors duration-200',
                      isActive
                        ? 'text-purple-600 dark:text-purple-400'
                        : 'text-purple-500 dark:text-purple-400'
                    )}
                  >
                    {label}
                  </span>
                </Link>
              );
            }

            return (
              <Link
                key={item.id}
                href={item.route}
                className="flex flex-col items-center justify-center gap-1 flex-1 py-1 group"
                aria-label={label}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon
                  className={clsx(
                    'w-6 h-6 transition-colors duration-200',
                    isActive
                      ? 'text-secondary dark:text-secondary'
                      : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                  )}
                />
                <span
                  className={clsx(
                    'text-[10px] font-medium leading-none transition-colors duration-200',
                    isActive
                      ? 'text-secondary font-semibold dark:text-secondary'
                      : 'text-gray-400 dark:text-gray-500'
                  )}
                >
                  {label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
